/**
 * L1J Web Client - Character Select Scene
 *
 * 로그인 직후 진입하는 캐릭터 선택 화면.
 * - 게이트웨이에서 char_count + char_list (캐릭터당 1회 누적)를 받아 카드 슬롯에 표시
 * - 라니지1 원본에 가까운 다크 골드 톤 UI
 *   * 좌측: 직업 아이콘 + 이름 + Lv/직업
 *   * 우측: HP/MP 게이지, AC, 성별, Lawful
 *   * 신규 캐릭터는 마지막 슬롯에 [ + CREATE ] 버튼으로 고정 노출
 */

import Phaser from 'phaser';
import { ConnectionManager } from '../../network/ConnectionManager';
import { CharInfo } from '../../types/network';
import { storageManager } from '../../core/StorageManager';
import {
  characterIdFromClass,
  getCharSpriteKey,
  loadCharSprite,
  preloadCharacterSprites,
  IDLE_FRAME,
} from '../../utils/spriteLoader';
import { SessionState } from '../../core/types';
import { isGuestAccount } from '../../utils/guest';

// 직업 ID 매핑 (서버 C_CreateChar.java type 순서 기준)
// 0=Royal, 1=Knight, 2=Elf, 3=Wizard, 4=Dark Elf, 5=Dragon Knight, 6=Illusionist
const CLASS_NAMES: Record<number, string> = {
  0: 'Royal',
  1: 'Knight',
  2: 'Elf',
  3: 'Wizard',
  4: 'Dark Elf',
  5: 'Dragon Knight',
  6: 'Illusionist',
};

const CLASS_COLORS: Record<number, { fill: number; glow: number; accent: string }> = {
  0: { fill: 0xc9a227, glow: 0xffd866, accent: '#c9a227' }, // Royal (gold)
  1: { fill: 0x2c5fa8, glow: 0x6aa8e8, accent: '#4488cc' }, // Knight (blue)
  2: { fill: 0x22a85a, glow: 0x66e890, accent: '#22cc66' }, // Elf (green)
  3: { fill: 0xa86224, glow: 0xe89858, accent: '#cc8844' }, // Wizard (orange)
  4: { fill: 0x8a3ad9, glow: 0xc088ff, accent: '#aa55ee' }, // Dark Elf (purple)
  5: { fill: 0xc8341a, glow: 0xff7050, accent: '#dd4422' }, // Dragon Knight (red)
  6: { fill: 0x1aa8a8, glow: 0x55e0e0, accent: '#22cccc' }, // Illusionist (cyan)
};

const CLASS_INITIALS: Record<number, string> = {
  0: 'R',  // Royal
  1: 'K',  // Knight
  2: 'E',  // Elf
  3: 'W',  // Wizard
  4: 'DE', // Dark Elf
  5: 'DK', // Dragon Knight
  6: 'I',  // Illusionist
};

const MAX_CHAR_SLOTS = 6;

// 카드 격자. 이 화면의 주인공은 캐릭터 그림이므로 가로로 눕힌 목록이 아니라
// 세로 카드를 3열로 세운다.
const CARD_COLS = 3;
const CARD_WIDTH = 280;
const CARD_HEIGHT = 250;
const CARD_GAP_X = 24;
const CARD_GAP_Y = 20;
const CARD_RADIUS = 10;
/** 격자 첫 행의 위쪽 경계 (헤더 밴드 아래). */
const GRID_TOP = 120;

/** 캐릭터 SVG 원본은 48px. 카드에서는 이만큼 키워 세운다. */
const SPRITE_SCALE = 2;
/** 스프라이트 중심의 카드 내 Y. */
const SPRITE_Y = -58;

/** 카드 프레임의 시각 상태. */
type CardState = 'empty' | 'idle' | 'hover' | 'selected';

interface CharCard {
  index: number;
  container: Phaser.GameObjects.Container;
  charInfo: CharInfo | null;
  // 텍스트/그래픽 참조 (갱신 시 재활용)
  cardBg: Phaser.GameObjects.Graphics;
  nameText: Phaser.GameObjects.Text;
  levelText: Phaser.GameObjects.Text;
  classText: Phaser.GameObjects.Text;
  hpFill: Phaser.GameObjects.Graphics;
  hpText: Phaser.GameObjects.Text;
  mpFill: Phaser.GameObjects.Graphics;
  mpText: Phaser.GameObjects.Text;
  acText: Phaser.GameObjects.Text;
  lawfulText: Phaser.GameObjects.Text;
  /** 캐릭터 그림. 텍스처가 준비되기 전에는 숨기고 iconText로 버틴다. */
  sprite: Phaser.GameObjects.Image;
  /** 이 카드가 표시해야 할 텍스처 키. 비동기 로드가 끝났을 때 아직 유효한지 확인용. */
  spriteKey: string | null;
  /** 스프라이트를 못 쓸 때 자리를 지키는 직업 이니셜. */
  iconText: Phaser.GameObjects.Text;
  hitArea: Phaser.GameObjects.Rectangle;
}

export class CharSelectScene extends Phaser.Scene {
  private connectionManager!: ConnectionManager;
  private cards: CharCard[] = [];
  private newCharCard!: Phaser.GameObjects.Container;
  private centerX = 0;
  private centerY = 0;

  private loadingText: Phaser.GameObjects.Text | null = null;
  private errorText: Phaser.GameObjects.Text | null = null;
  private titleText!: Phaser.GameObjects.Text;
  private subtitleText!: Phaser.GameObjects.Text;
  private footerText!: Phaser.GameObjects.Text;

  private isLoading = true;
  private enteredGame = false;
  private selectedIndex = -1;
  private hasNewCharCard = false;
  /**
   * 게스트 자동 진입(캐릭터 생성 또는 자동 선택)을 이미 시작했는지.
   *
   * char_list는 캐릭터마다 한 번씩 도착해 화면을 여러 번 다시 그리므로, 이 가드가
   * 없으면 생성 요청이 중복으로 나간다.
   *
   * **create()의 리셋 목록에 일부러 넣지 않았다.** Phaser는 씬 인스턴스를 재사용하니
   * 이 값은 페이지를 여는 동안 유지되고, 그래서 자동 진입은 한 번뿐이다. 죽어서
   * 캐릭터 선택으로 돌아온 사람을 곧바로 월드에 다시 밀어 넣으면 화면을 볼 수도,
   * 다른 선택을 할 수도 없다.
   */
  private guestAutoFlowStarted = false;

  /**
   * 스프라이트를 이미 미리 받은 "클래스|성별" 조합.
   * handleCharList가 여러 번 호출되므로 같은 조합을 반복 요청하지 않기 위해 둔다.
   */
  private preloadedCharKeys = new Set<string>();

  /** ConnectionManager에 등록한 리스너 (shutdown 시 동일 참조로 해제) */
  private connectionListeners: Array<{ event: string; handler: (data: unknown) => void }> = [];

  constructor() {
    super({ key: 'CharSelectScene' });
  }

  // ============================================================
  // 라이프사이클
  // ============================================================

  create(): void {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;
    this.centerX = width / 2;
    this.centerY = height / 2;

    this.cameras.main.setBackgroundColor(0x07070d);

    // scene.restart() 또는 create() 재호출 시 this.cards 가 누적되지 않도록 reset.
    // 만약 이전 카드가 남아있다면 destroy하고 새로 시작한다.
    if (this.cards && this.cards.length > 0) {
      this.cards.forEach((c) => {
        try { c.container?.destroy(); } catch { /* ignore */ }
      });
      this.cards = [];
    }

    // 씬 재진입 시 이전 실행의 상태/파괴된 GameObject 참조가 남지 않도록 리셋
    this.enteredGame = false;
    this.isLoading = true;
    this.selectedIndex = -1;
    this.hasNewCharCard = false;
    this.loadingText = null; // 이전 씬 종료 시 destroy된 참조
    this.errorText = null;   // 이전 씬 종료 시 destroy된 참조
    this.connectionListeners = [];

    this.connectionManager = ConnectionManager.getInstance();
    this.drawBackground(width, height);
    this.drawHeader();
    this.drawFooter();
    this.createSlotCards();
    this.showLoading('DB에서 캐릭터 정보를 불러오는 중...');

    this.registerEventListeners();
    this.startCharListTimeout();

    // Phaser는 shutdown() 메서드를 자동 호출하지 않으므로 이벤트로 정리 훅 등록
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.cleanup, this);

    // 로그인 직후 char_list가 create() 호출 전에 도착했을 수 있으므로
    // PacketHandler에 캐시된 데이터를 즉시 가져와서 표시한다.
    this.renderCharListFromCache();
  }

  /**
   * PacketHandler가 누적한 캐릭터 목록으로 카드를 다시 그린다.
   *
   * **화면 갱신은 항상 이 캐시를 거쳐야 한다.** 게이트웨이는 캐릭터 1명당
   * char_list를 한 번씩 보내므로(서버가 S_CharPack을 캐릭터마다 따로 보낸다),
   * 개별 메시지의 payload로 카드를 그리면 매번 화면이 덮어써져 마지막 한 명만 남는다.
   *
   * 두 시점에서 호출된다:
   *  - 씬 시작 직후: 리스너 등록 전에 도착해 누락된 목록을 복구
   *  - char_list 수신 시: 그 시점까지 누적된 전체 목록으로 다시 그림
   */
  private renderCharListFromCache(listArrived: boolean = false): void {
    const handler = this.connectionManager.getPacketHandler?.();
    if (!handler) return;
    const cached = handler.getCharList();
    if (cached.length > 0) {
      console.log('[CharSelectScene] renderCharListFromCache:', cached.length, 'chars');
      this.handleCharList(cached);
      return;
    }

    // 여기서부터는 캐시가 비어 있다. "캐릭터 없음"일 수도, "아직 안 받음"일 수도 있다.
    //
    //  - char_list 수신(listArrived=true): 방금 확정된 목록이 비었으니 진짜 0개다.
    //  - 씬 시작 직후: char_count는 로그인 직후 곧바로 와서 이 씬이 리스너를 걸기
    //    전에 지나가기 쉽다. 그래서 캐시를 되짚는데, 이때 빈 배열을 곧이곧대로
    //    "0개"로 읽으면 재로그인 직후처럼 목록이 아직 오지 않은 경우에 빈 화면이
    //    굳어 버린다(캐릭터가 멀쩡히 있는데도). 확정 여부는 PacketHandler만 안다.
    if (!listArrived && !handler.isCharListResolved()) return;

    console.log('[CharSelectScene] renderCharListFromCache: 캐릭터 없음 (빈 목록 확정)');
    this.handleCharList([]);
  }

  // ============================================================
  // 배경 / 헤더 / 푸터
  // ============================================================

  private drawBackground(width: number, height: number): void {
    const g = this.add.graphics();

    // 상단 다크 블루 그라데이션
    g.fillGradientStyle(0x14142a, 0x14142a, 0x0a0a18, 0x0a0a18, 1);
    g.fillRect(0, 0, width, height / 2);

    // 하단 더 어둡게
    g.fillGradientStyle(0x0d0d20, 0x0d0d20, 0x05050b, 0x05050b, 1);
    g.fillRect(0, height / 2, width, height / 2);

    // 미세한 격자 라인
    g.lineStyle(1, 0x2a2a44, 0.25);
    for (let y = 0; y < height; y += 48) {
      g.lineBetween(0, y, width, y);
    }

    // 가장자리 프레임 (금색 라인)
    g.lineStyle(2, 0x6a5a1c, 0.8);
    g.strokeRect(8, 8, width - 16, height - 16);

    // 코너 장식
    this.drawCorner(g, 8, 8, 1, 1);
    this.drawCorner(g, width - 8, 8, -1, 1);
    this.drawCorner(g, 8, height - 8, 1, -1);
    this.drawCorner(g, width - 8, height - 8, -1, -1);
  }

  private drawCorner(
    g: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    sx: number,
    sy: number
  ): void {
    const size = 18;
    g.lineStyle(2, 0xc9a227, 1);
    g.lineBetween(x, y, x + sx * size, y);
    g.lineBetween(x, y, x, y + sy * size);
  }

  private drawHeader(): void {
    // 상단 백금 띠
    const band = this.add.graphics();
    band.fillStyle(0x14142a, 0.85);
    band.fillRect(0, 0, this.cameras.main.width, 96);
    band.lineStyle(2, 0x6a5a1c, 1);
    band.lineBetween(0, 96, this.cameras.main.width, 96);

    this.titleText = this.add.text(this.centerX, 40, 'SELECT YOUR CHARACTER', {
      fontSize: '28px',
      fontFamily: 'Georgia, serif',
      fontStyle: 'bold',
      color: '#e8c84a',
      stroke: '#000000',
      strokeThickness: 4,
    }).setOrigin(0.5);

    this.subtitleText = this.add.text(this.centerX, 72, '— Choose your champion —', {
      fontSize: '13px',
      fontFamily: 'Georgia, serif',
      fontStyle: 'italic',
      color: '#8888aa',
    }).setOrigin(0.5);

    // 로그아웃 버튼
    const logoutBtn = this.add.text(28, 32, '◀ LOGOUT', {
      fontSize: '15px',
      fontFamily: 'Georgia, serif',
      color: '#a89668',
      backgroundColor: '#0a0a18',
      padding: { x: 12, y: 6 },
    }).setOrigin(0, 0.5).setInteractive({ useHandCursor: true });

    logoutBtn.on('pointerover', () => logoutBtn.setColor('#ffffff'));
    logoutBtn.on('pointerout', () => logoutBtn.setColor('#a89668'));
    logoutBtn.on('pointerdown', () => {
      storageManager.clearCredentials();
      this.connectionManager.disconnect();
      this.scene.start('LoginScene');
    });
  }

  private drawFooter(): void {
    const y = this.cameras.main.height - 28;

    const band = this.add.graphics();
    band.fillStyle(0x0a0a18, 0.85);
    band.fillRect(0, y - 18, this.cameras.main.width, 50);
    band.lineStyle(1, 0x3a3a5a, 0.6);
    band.lineBetween(0, y - 18, this.cameras.main.width, y - 18);

    this.footerText = this.add.text(this.centerX, y, '', {
      fontSize: '13px',
      fontFamily: 'Georgia, serif',
      fontStyle: 'italic',
      color: '#8888aa',
    }).setOrigin(0.5);

    this.setFooter('캐릭터를 선택한 뒤 다시 클릭하면 게임에 접속합니다.');
  }

  private setFooter(msg: string, color = '#8888aa'): void {
    this.footerText.setText(msg).setColor(color);
  }

  // ============================================================
  // 슬롯 카드
  // ============================================================

  private createSlotCards(): void {
    // 이미 생성되어 있으면 중복 생성 방지 (scene 재시작 안전망)
    if (this.cards.length > 0) return;

    const totalWidth = CARD_COLS * CARD_WIDTH + (CARD_COLS - 1) * CARD_GAP_X;
    const startX = this.centerX - totalWidth / 2 + CARD_WIDTH / 2;
    const startY = GRID_TOP + CARD_HEIGHT / 2;

    for (let i = 0; i < MAX_CHAR_SLOTS; i++) {
      const col = i % CARD_COLS;
      const row = Math.floor(i / CARD_COLS);
      const x = startX + col * (CARD_WIDTH + CARD_GAP_X);
      const y = startY + row * (CARD_HEIGHT + CARD_GAP_Y);

      this.cards.push(this.createEmptyCard(i, x, y));
    }

    // 새 캐릭터는 격자 안의 칸이 아니라 격자 아래의 버튼이다.
    // 6칸이 모두 슬롯이므로 일곱 번째 칸을 만들 자리가 없고, 성격도 다르다.
    const gridBottom = startY + (Math.ceil(MAX_CHAR_SLOTS / CARD_COLS) - 1) * (CARD_HEIGHT + CARD_GAP_Y) + CARD_HEIGHT / 2;
    this.newCharCard = this.createNewCharCard(this.centerX, gridBottom + 34, CARD_WIDTH, 40);
  }

  /**
   * 카드 프레임(배경 + 테두리 + 직업 광원)을 그린다.
   *
   * 예전에는 빈 슬롯/기본/hover/선택 네 상태를 각자 그려서 카드 크기가 네 군데에
   * 하드코딩돼 있었다. 상태별 색만 다르고 형태는 같으므로 한곳에서 그린다.
   */
  private drawCardFrame(card: CharCard, state: CardState): void {
    const g = card.cardBg;
    const half = { w: CARD_WIDTH / 2, h: CARD_HEIGHT / 2 };
    const colors = card.charInfo
      ? CLASS_COLORS[card.charInfo.type] ?? { fill: 0x888888, glow: 0xaaaaaa, accent: '#aaaaaa' }
      : null;

    const fill = { empty: 0x0e0d14, idle: 0x16141c, hover: 0x1d1a26, selected: 0x241f2e }[state];
    const fillAlpha = state === 'empty' ? 0.35 : 0.96;

    g.clear();
    g.fillStyle(fill, fillAlpha);
    g.fillRoundedRect(-half.w, -half.h, CARD_WIDTH, CARD_HEIGHT, CARD_RADIUS);

    // 직업 광원 — 캐릭터 뒤에서 은은하게. 직업 색을 여기서만 쓴다.
    if (colors && state !== 'empty') {
      // 반지름은 카드 상단(-CARD_HEIGHT/2)을 넘지 않아야 한다. 넘치면 광원이
      // 카드 밖으로 새어 카드 경계가 무너져 보인다.
      const glowAlpha = state === 'idle' ? 0.1 : 0.18;
      g.fillStyle(colors.fill, glowAlpha);
      g.fillCircle(0, SPRITE_Y, 46);
      g.fillStyle(colors.fill, glowAlpha * 0.55);
      g.fillCircle(0, SPRITE_Y, 62);
    }

    const border = {
      empty: { color: 0x1e1a26, width: 1, alpha: 0.6 },
      idle: { color: colors?.fill ?? 0x2a2530, width: 1, alpha: 0.55 },
      hover: { color: 0xc9a227, width: 2, alpha: 0.95 },
      selected: { color: 0xf0d77a, width: 2.5, alpha: 1 },
    }[state];

    g.lineStyle(border.width, border.color, border.alpha);
    g.strokeRoundedRect(-half.w, -half.h, CARD_WIDTH, CARD_HEIGHT, CARD_RADIUS);
  }

  private createEmptyCard(index: number, x: number, y: number): CharCard {
    const container = this.add.container(x, y);

    const cardBg = this.add.graphics();
    container.add(cardBg);

    // 캐릭터 그림 — 이 카드의 주인공.
    const sprite = this.add.image(0, SPRITE_Y, '__placeholder_char__');
    sprite.setOrigin(0.5, 0.5);
    sprite.setScale(SPRITE_SCALE);
    sprite.setVisible(false);
    container.add(sprite);

    // 스프라이트를 못 쓸 때만 보이는 직업 이니셜.
    const iconText = this.add.text(0, SPRITE_Y, '', {
      fontSize: '34px',
      fontFamily: 'Georgia, serif',
      fontStyle: 'bold',
      color: '#3a3446',
    }).setOrigin(0.5);
    container.add(iconText);

    const nameText = this.add.text(0, 10, '빈 슬롯', {
      fontSize: '17px',
      fontFamily: 'Georgia, serif',
      fontStyle: 'bold',
      color: '#4e4859',
    }).setOrigin(0.5);
    container.add(nameText);

    // 직업(좌) / 레벨(우) — 같은 줄에서 양끝 정렬
    const classText = this.add.text(-CARD_WIDTH / 2 + 22, 34, '', {
      fontSize: '11px',
      fontFamily: 'Arial, sans-serif',
      color: '#6b6478',
    }).setOrigin(0, 0.5);
    container.add(classText);

    // 수치는 고정폭으로 — 카드마다 자릿수가 달라도 오른끝이 흔들리지 않는다.
    const levelText = this.add.text(CARD_WIDTH / 2 - 22, 34, '', {
      fontSize: '11px',
      fontFamily: '"Courier New", monospace',
      color: '#c9a227',
    }).setOrigin(1, 0.5);
    container.add(levelText);

    const hpText = this.add.text(CARD_WIDTH / 2 - 22, 58, '', {
      fontSize: '10px',
      fontFamily: '"Courier New", monospace',
      color: '#7fbf7f',
    }).setOrigin(1, 0.5);
    container.add(hpText);

    const hpFill = this.add.graphics();
    container.add(hpFill);

    const mpText = this.add.text(CARD_WIDTH / 2 - 22, 84, '', {
      fontSize: '10px',
      fontFamily: '"Courier New", monospace',
      color: '#7f9fdf',
    }).setOrigin(1, 0.5);
    container.add(mpText);

    const mpFill = this.add.graphics();
    container.add(mpFill);

    const acText = this.add.text(-CARD_WIDTH / 2 + 22, 106, '', {
      fontSize: '10px',
      fontFamily: '"Courier New", monospace',
      color: '#6b6478',
    }).setOrigin(0, 0.5);
    container.add(acText);

    const lawfulText = this.add.text(CARD_WIDTH / 2 - 22, 106, '', {
      fontSize: '10px',
      fontFamily: '"Courier New", monospace',
      color: '#6b6478',
    }).setOrigin(1, 0.5);
    container.add(lawfulText);

    const hitArea = this.add.rectangle(0, 0, CARD_WIDTH, CARD_HEIGHT, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    container.add(hitArea);

    const card: CharCard = {
      index,
      container,
      charInfo: null,
      cardBg,
      nameText,
      levelText,
      classText,
      hpFill,
      hpText,
      mpFill,
      mpText,
      acText,
      lawfulText,
      sprite,
      spriteKey: null,
      iconText,
      hitArea,
    };

    this.drawCardFrame(card, 'empty');
    return card;
  }

  /**
   * 새 캐릭터 만들기 버튼 (격자 아래).
   *
   * 슬롯 카드와 같은 크기로 두면 "일곱 번째 캐릭터"처럼 보인다.
   * 성격이 다른 동작이므로 납작한 버튼으로 낮춰 격자와 구분한다.
   */
  private createNewCharCard(
    x: number,
    y: number,
    width: number,
    height: number
  ): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    const bg = this.add.graphics();
    container.add(bg);

    const label = this.add.text(0, 0, '+  새 캐릭터 만들기', {
      fontSize: '13px',
      fontFamily: 'Georgia, serif',
      color: '#a89668',
    }).setOrigin(0.5);
    container.add(label);

    const paint = (on: boolean) => {
      bg.clear();
      bg.fillStyle(on ? 0x1d1a26 : 0x121019, 0.9);
      bg.fillRoundedRect(-width / 2, -height / 2, width, height, 6);
      bg.lineStyle(1, on ? 0xc9a227 : 0x4a3f24, on ? 1 : 0.8);
      bg.strokeRoundedRect(-width / 2, -height / 2, width, height, 6);
      label.setColor(on ? '#f0d77a' : '#a89668');
    };
    paint(false);

    const hitArea = this.add.rectangle(0, 0, width, height, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    container.add(hitArea);

    hitArea.on('pointerover', () => paint(true));
    hitArea.on('pointerout', () => paint(false));
    hitArea.on('pointerdown', () => {
      this.scene.start('CharCreateScene');
    });

    container.setVisible(false); // 빈 슬롯이 있을 때만 노출
    return container;
  }

  // ============================================================
  // 이벤트 처리
  // ============================================================

  /**
   * ConnectionManager 구독 + shutdown 시 해제를 위한 참조 추적 헬퍼.
   */
  private onNet(event: string, handler: (data: unknown) => void): void {
    this.connectionManager.on(event, handler);
    this.connectionListeners.push({ event, handler });
  }

  private registerEventListeners(): void {
    // payload는 캐릭터 1명짜리 배열이다. 그대로 그리면 마지막 한 명만 남으므로
    // (월드에서 캐릭터 선택으로 복귀할 때 이 경로로 목록이 다시 온다)
    // PacketHandler가 누적한 전체 목록으로 다시 그린다.
    this.onNet('char_list', () => {
      this.renderCharListFromCache(true);
    });

    this.onNet('char_spawn', (payload) => {
      this.handleCharSpawn(payload);
    });

    // 게스트 캐릭터를 이 씬에서 직접 만들므로 그 결과도 여기서 받는다
    // (사람이 CharCreateScene을 거쳐 만드는 경로는 그 씬이 따로 처리한다).
    this.onNet('char_create_result', (payload) => {
      this.handleGuestCreateResult(payload as { result: number; message?: string });
    });

    // 게이트웨이는 char_spawn 대신 own_char_status(opcode 145)로 인게임 진입을
    // 알린다. 이 신호를 받아야 "DB 접속중..." 로딩에서 GameScene으로 넘어간다.
    this.onNet('own_char_status', (payload) => {
      this.handleCharSpawn(payload);
    });

    this.onNet('error', (payload) => {
      const error = payload as { message: string };
      this.showError(`오류: ${error.message}`);
    });

    this.onNet('disconnected', () => {
      this.showError('서버 연결이 끊어졌습니다.');
    });

    // 다른 곳에서 같은 계정으로 로그인해 이 세션이 강제 종료된 경우.
    // 계정/비밀번호 자체는 여전히 유효하므로 저장된 자격증명은 지우지 않는다 —
    // 단, 로그인 화면이 자동으로 다시 로그인 시도를 하면 방금 접속한 다른
    // 세션을 도로 끊어버릴 수 있으므로 자동 로그인은 건너뛰도록 신호를 남긴다.
    this.onNet('force_logout', (payload) => {
      const message = (payload as { message?: string })?.message
        || '다른 곳에서 같은 계정으로 로그인하여 연결이 종료되었습니다.';
      this.showError(message, '#ffcc44');
      storageManager.suppressNextAutoLogin();
      this.time.delayedCall(1500, () => {
        this.connectionManager.disconnect();
        this.scene.start('LoginScene');
      });
    });
  }

  /**
   * 목록이 도착할 때까지 캐시를 되짚고, 끝내 오지 않으면 에러를 띄운다.
   *
   * char_list 이벤트만 믿을 수 없다. 이 씬은 `scene.start()`로 시작되어 다음
   * 프레임에야 create()가 도는데, 서버 응답은 그보다 빨리 올 수 있다. 그 틈에
   * 도착한 이벤트는 리스너가 없어 사라지고, create()가 캐시를 읽는 시점에는 아직
   * 반영 전이라 화면이 빈 채로 남는다 — 캐릭터를 만들자마자 죽어서 돌아온 경우가
   * 바로 이 틈이었다(생성 직후에는 서버가 목록을 다시 보내주지 않아 캐시도 비어 있다).
   *
   * 그래서 이벤트를 기다리기만 하지 않고 짧은 주기로 캐시를 확인한다.
   */
  private startCharListTimeout(): void {
    const POLL_MS = 700;
    const MAX_TRIES = Math.ceil(8000 / POLL_MS); // 기존과 같은 8초 한도

    let tries = 0;
    const poll = this.time.addEvent({
      delay: POLL_MS,
      loop: true,
      callback: () => {
        tries++;
        const handler = this.connectionManager.getPacketHandler?.();
        if (handler && (handler.getCharList().length > 0 || handler.isCharListResolved())) {
          poll.remove();
          this.renderCharListFromCache(true);
          return;
        }
        if (tries >= MAX_TRIES) {
          poll.remove();
          if (this.isLoading) {
            this.hideLoading();
            this.renderEmpty();
            this.showError('서버에서 응답이 없습니다.', '#ff8866');
          }
        }
      },
    });
  }

  /**
   * 목록에 있는 캐릭터들의 SVG 스프라이트를 미리 받아둔다.
   *
   * 월드 진입 직후에는 지연 로딩 때문에 캐릭터가 한 박자 늦게 나타난다.
   * 캐릭터 선택 화면은 네트워크가 한가하고 사용자가 어차피 머무는 구간이라
   * 여기서 채워두면 진입이 매끄러워진다.
   *
   * await하지 않는다 — 카드 렌더링을 막으면 안 된다. 실패해도 GameScene이
   * 필요할 때 다시 요청하므로 무시한다.
   */
  private preloadSpritesFor(charList: CharInfo[]): void {
    for (const char of charList) {
      // 같은 클래스/성별이면 스프라이트가 같으므로 한 번만 받는다.
      const cacheKey = `${char.type}|${char.sex}`;
      if (this.preloadedCharKeys.has(cacheKey)) continue;
      this.preloadedCharKeys.add(cacheKey);

      const characterId = characterIdFromClass(char.type, char.sex);
      preloadCharacterSprites(this, characterId)
        .then((count) => {
          console.log(`[CharSelectScene] 스프라이트 프리로드 완료: ${characterId} (${count}개)`);
        })
        .catch(() => {
          // 프리로드 실패는 진행을 막지 않는다.
        });
    }
  }

  private handleCharList(charList: CharInfo[]): void {
    this.isLoading = false;
    this.hideLoading();
    console.log('[CharSelectScene] Received char list:', charList);
    console.log('[CharSelectScene] this.cards.length =', this.cards.length);

    if (!charList || charList.length === 0) {
      this.renderEmpty();
      // 체험 계정은 캐릭터를 고르는 화면 없이 바로 월드로 들어가야 하므로,
      // 첫 방문이면 여기서 캐릭터를 만들어 준다.
      this.maybeAutoCreateGuestCharacter();
      return;
    }

    this.populateCards(charList);

    // 두 번째 방문부터는 이미 만들어 둔 캐릭터로 그대로 들어간다.
    this.maybeAutoSelectGuestCharacter(charList);

    // 목록이 확정된 시점에 스프라이트를 미리 받아둔다 (화면을 막지 않는다).
    this.preloadSpritesFor(charList);

    // 진단: 카드 가시성/위치 확인
    this.cards.forEach((c, i) => {
      console.log(`[CharSelectScene] card[${i}] visible=${c.container.visible} x=${c.container.x} y=${c.container.y} depth=${c.container.depth} scaleX=${c.container.scaleX} scaleY=${c.container.scaleY} alpha=${c.container.alpha} charInfo=${c.charInfo?.name ?? 'null'}`);
    });
  }

  private handleCharSpawn(payload: unknown): void {
    // own_char_status는 인게임 진입 후에도 주기적으로 재전송되므로,
    // 이미 GameScene으로 넘어갔다면 다시 반응하지 않는다 (씬 재시작 방지).
    if (this.enteredGame) return;
    this.enteredGame = true;

    this.hideLoading();
    console.log('[CharSelectScene] Char spawn → GameScene', payload);
    this.scene.start('GameScene', { charData: payload });
  }

  // ============================================================
  // 카드 갱신
  // ============================================================

  private populateCards(charList: CharInfo[]): void {
    // 초기화 — 각 카드를 개별 try-catch로 감싸서
    // 한 카드에서 setColor/setText frame2 null 에러가 발생해도
    // 다른 카드 처리는 계속되도록 한다.
    for (const card of this.cards) {
      try {
        this.applyCardData(card, null);
      } catch (e) {
        console.warn(`[CharSelectScene] applyCardData(empty) error for card ${card.index}:`, e);
      }
    }

    charList.slice(0, MAX_CHAR_SLOTS).forEach((char, i) => {
      try {
        this.applyCardData(this.cards[i], char);
      } catch (e) {
        console.warn(`[CharSelectScene] applyCardData(char) error for card ${i}:`, e);
      }
    });

    // 신규 캐릭터 카드 노출 여부
    const showNew = charList.length < MAX_CHAR_SLOTS;
    this.newCharCard.setVisible(showNew);
    this.hasNewCharCard = showNew;

    // 부제목에 카운트 표시
    this.subtitleText.setText(`${MAX_CHAR_SLOTS}칸 중 ${charList.length}칸 사용`);
    this.setFooter('캐릭터를 고르고 한 번 더 누르면 접속합니다.');

    // 모든 카드 인터랙션 바인딩 (populate 시점 한 번만)
    this.attachCardHandlers();
  }

  private renderEmpty(): void {
    for (const card of this.cards) {
      this.applyCardData(card, null);
    }
    this.subtitleText.setText(`${MAX_CHAR_SLOTS}칸 중 0칸 사용`);
    this.newCharCard.setVisible(true);
    this.hasNewCharCard = true;
    this.setFooter('아래 버튼으로 첫 캐릭터를 만드세요.');
    this.attachCardHandlers();
  }

  private applyCardData(card: CharCard, charInfo: CharInfo | null): void {
    card.charInfo = charInfo;

    if (!charInfo) {
      card.spriteKey = null;
      card.sprite.setVisible(false);
      card.iconText.setText('');

      this.drawCardFrame(card, 'empty');

      card.nameText.setText('빈 슬롯').setColor('#3f3a4a');
      card.levelText.setText('');
      card.classText.setText('');
      card.hpText.setText('');
      card.mpText.setText('');
      card.acText.setText('');
      card.lawfulText.setText('');
      card.hpFill.clear();
      card.mpFill.clear();
      return;
    }

    const colors = CLASS_COLORS[charInfo.type] ?? {
      fill: 0x888888,
      glow: 0xaaaaaa,
      accent: '#aaaaaa',
    };
    const className = CLASS_NAMES[charInfo.type] ?? `Class ${charInfo.type}`;

    this.drawCardFrame(card, 'idle');
    this.applyCardSprite(card, charInfo);

    card.nameText.setText(charInfo.name).setColor('#f2eee6');
    card.classText.setText(className).setColor(colors.accent);
    card.levelText.setText(`Lv ${charInfo.level}`).setColor('#c9a227');

    // 캐릭터 목록은 최대치만 알려준다 (현재값 없음) — 가득 찬 바로 그린다.
    const barX = -CARD_WIDTH / 2 + 22;
    const barW = CARD_WIDTH - 44;
    const hpMax = charInfo.hp > 0 ? charInfo.hp : 1;
    const mpMax = charInfo.mp > 0 ? charInfo.mp : 1;

    this.drawBar(card.hpFill, barX, 66, barW, 4, hpMax, hpMax, 0x4c9a4c, 0x1e3d1e);
    card.hpText.setText(`HP ${hpMax}`);

    this.drawBar(card.mpFill, barX, 92, barW, 4, mpMax, mpMax, 0x4472b8, 0x1e2f4d);
    card.mpText.setText(`MP ${mpMax}`);

    card.acText.setText(`AC ${charInfo.ac}`);

    const lawful = charInfo.lawful;
    card.lawfulText
      .setText(lawful >= 0 ? `선 ${lawful}` : `악 ${lawful}`)
      .setColor(lawful >= 0 ? '#7fbf7f' : '#c46b6b');
  }

  /**
   * 카드에 캐릭터 그림을 붙인다.
   *
   * 텍스처는 로그인 직후 preloadSpritesFor가 미리 받아두므로 대개 즉시 붙는다.
   * 아직 없으면 비동기로 받아서 붙이되, 그 사이 카드 내용이 바뀌었으면 버린다.
   * 끝내 실패하면 직업 이니셜로 자리를 지킨다 — 카드가 비어 보이지 않게.
   */
  private applyCardSprite(card: CharCard, charInfo: CharInfo): void {
    const characterId = characterIdFromClass(charInfo.type, charInfo.sex);
    const key = getCharSpriteKey(characterId, 'none', 'S', IDLE_FRAME);
    card.spriteKey = key;

    const show = () => {
      if (card.spriteKey !== key) return; // 그 사이 다른 캐릭터로 바뀜
      card.sprite.setTexture(key);
      // 도트 그림을 2배로 세우므로 선형 보간을 끈다. 켜두면 윤곽이 뭉개진다.
      card.sprite.texture.setFilter(Phaser.Textures.FilterMode.NEAREST);
      card.sprite.setVisible(true);
      card.iconText.setText('');
    };

    if (this.textures.exists(key)) {
      show();
      return;
    }

    // 로드되기 전까지는 이니셜을 세워 둔다.
    card.sprite.setVisible(false);
    card.iconText.setText(CLASS_INITIALS[charInfo.type] ?? '?');

    loadCharSprite(this, characterId, 'none', 'S', IDLE_FRAME)
      .then(show)
      .catch(() => {
        // 이니셜 폴백을 그대로 둔다.
      });
  }

  private drawBar(
    g: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    width: number,
    height: number,
    current: number,
    max: number,
    fillColor: number,
    borderColor: number
  ): void {
    g.clear();
    // 배경
    g.fillStyle(0x05050b, 0.95);
    g.fillRect(x, y, width, height);
    // 채움
    const ratio = Math.max(0, Math.min(1, current / max));
    g.fillStyle(fillColor, 0.85);
    g.fillRect(x + 1, y + 1, (width - 2) * ratio, height - 2);
    // 테두리
    g.lineStyle(1, borderColor, 1);
    g.strokeRect(x, y, width, height);
  }

  // ============================================================
  // 선택 / 클릭
  // ============================================================

  private attachCardHandlers(): void {
    for (const card of this.cards) {
      // 기존 핸들러 제거 후 재바인딩 (중복 방지)
      card.hitArea.removeAllListeners();
      card.hitArea.on('pointerover', () => this.onCardHover(card, true));
      card.hitArea.on('pointerout', () => this.onCardHover(card, false));
      card.hitArea.on('pointerdown', () => this.onCardClick(card));
    }
  }

  private onCardHover(card: CharCard, hover: boolean): void {
    if (!card.charInfo) return;
    if (this.selectedIndex === card.index) return;

    this.drawCardFrame(card, hover ? 'hover' : 'idle');
    // 마우스를 올리면 캐릭터가 한 발 앞으로 나오는 정도의 반응만 준다.
    card.sprite.setScale(hover ? SPRITE_SCALE * 1.06 : SPRITE_SCALE);
  }

  private onCardClick(card: CharCard): void {
    if (!card.charInfo) {
      this.showError('빈 슬롯입니다. 아래 버튼으로 새 캐릭터를 만드세요.');
      return;
    }

    if (this.selectedIndex === card.index) {
      // 더블 클릭 → 접속
      this.selectCharacter(card.charInfo.name);
      return;
    }

    this.clearSelection();
    this.selectedIndex = card.index;

    this.drawCardFrame(card, 'selected');
    card.sprite.setScale(SPRITE_SCALE * 1.12);

    this.setFooter(`${card.charInfo.name} — 한 번 더 누르면 접속합니다.`, '#f0d77a');
  }

  private clearSelection(): void {
    if (this.selectedIndex >= 0) {
      const prev = this.cards[this.selectedIndex];
      if (prev) {
        this.applyCardData(prev, prev.charInfo);
        prev.sprite.setScale(SPRITE_SCALE);
      }
    }
    this.selectedIndex = -1;
    this.setFooter('캐릭터를 고르고 한 번 더 누르면 접속합니다.');
  }

  // ============================================================
  // 체험용 게스트 계정 — 캐릭터 자동 생성 / 자동 선택
  // ============================================================

  /** 지금 로그인한 계정이 체험용 게스트인지. */
  private isGuestSession(): boolean {
    const account = storageManager.loadCredentials()?.account;
    return isGuestAccount(account);
  }

  /**
   * 게스트 계정에 캐릭터가 하나도 없으면 계정과 같은 이름으로 하나 만든다.
   *
   * 이름을 계정명과 같게 두는 이유는 체험 계정을 화면에서 바로 알아볼 수 있게
   * 하려는 것이고, 서버가 `_`를 허용하도록 `C_CreateChar.isAlphaNumeric()`을
   * 함께 고쳤다. 스탯은 군주(type 0)의 기본값에 보너스 8을 얹어 합계 75를
   * 맞춘다 — 서버 `C_CreateChar`가 `statusAmount != 75`면 거부한다.
   */
  private maybeAutoCreateGuestCharacter(): void {
    if (!this.isGuestSession()) return;
    if (this.guestAutoFlowStarted) return;
    this.guestAutoFlowStarted = true;

    const account = storageManager.loadCredentials()?.account;
    if (!account) return;

    this.showLoading('체험 캐릭터를 만드는 중...');

    // 군주 기본값 STR13 DEX10 CON10 WIS11 CHA13 INT10 (합 67) + 보너스 8 = 75
    const stats = [16, 12, 13, 11, 13, 10];

    console.log('[CharSelectScene] 게스트 캐릭터 자동 생성:', account);
    try {
      this.connectionManager.send({
        type: 'new_char',
        payload: { name: account, type: 0, sex: 0, stats },
      });
    } catch (err) {
      this.hideLoading();
      this.showError('체험 캐릭터 생성 요청 실패');
    }
  }

  /** 게스트 계정이면 목록의 첫 캐릭터로 곧바로 들어간다. */
  private maybeAutoSelectGuestCharacter(charList: CharInfo[]): void {
    if (!this.isGuestSession()) return;
    if (this.guestAutoFlowStarted) return;
    this.guestAutoFlowStarted = true;

    const first = charList[0];
    if (!first) return;

    console.log('[CharSelectScene] 게스트 캐릭터 자동 선택:', first.name);
    this.selectCharacter(first.name);
  }

  /**
   * 게스트 캐릭터 생성 결과 처리. 성공하면 그대로 월드에 들어간다.
   *
   * 서버는 생성에 성공해도 새 char_list를 보내 주지 않으므로, 여기서 방금 만든
   * 이름으로 곧장 select_char를 보낸다.
   */
  private handleGuestCreateResult(payload: { result: number; message?: string }): void {
    if (!this.guestAutoFlowStarted) return;

    const account = storageManager.loadCredentials()?.account;
    if (!account) return;

    if (payload.result === 0) {
      console.log('[CharSelectScene] 게스트 캐릭터 생성 성공 → 접속');
      this.selectCharacter(account);
      return;
    }

    this.hideLoading();
    this.showError(payload.message || '체험 캐릭터를 만들지 못했습니다.');
  }

  private selectCharacter(charName: string): void {
    this.showLoading(`${charName} — DB 접속 중...`);

    const creds = storageManager.loadCredentials();
    if (creds) {
      storageManager.saveCredentials(creds.account, creds.password, SessionState.IN_GAME, charName);
    }

    try {
      this.connectionManager.send({
        type: 'select_char',
        payload: { charName },
      });
    } catch (err) {
      this.hideLoading();
      this.showError('캐릭터 선택 요청 실패');
    }
  }

  // ============================================================
  // 로딩 / 에러 / 종료
  // ============================================================

  private showLoading(message: string): void {
    const text = this.loadingText ?? this.add
      .text(this.centerX, this.centerY, '', {
        fontSize: '16px',
        fontFamily: 'Georgia, serif',
        color: '#ffffff',
        backgroundColor: '#000000cc',
        padding: { x: 18, y: 10 },
      })
      .setOrigin(0.5)
      .setDepth(200);
    this.loadingText = text;
    text.setText(message).setVisible(true);
  }

  private hideLoading(): void {
    if (this.loadingText) this.loadingText.setVisible(false);
  }

  private showError(message: string, color = '#ff7777'): void {
    const text = this.errorText ?? this.add
      .text(this.centerX, this.cameras.main.height - 56, '', {
        fontSize: '14px',
        fontFamily: 'Georgia, serif',
        color,
        backgroundColor: '#1a0a0acc',
        padding: { x: 14, y: 6 },
      })
      .setOrigin(0.5)
      .setDepth(150);
    this.errorText = text;
    text.setText(message).setColor(color).setVisible(true);

    this.time.delayedCall(3500, () => {
      this.errorText?.setVisible(false);
    });
  }

  /**
   * 씬 종료(shutdown 이벤트) 시 정리.
   * 등록 시 보관한 동일 핸들러 참조로 off()해야 실제로 해제된다.
   * (익명 함수를 새로 만들어 off()에 넘기면 no-op이 된다.)
   */
  private cleanup(): void {
    this.connectionListeners.forEach(({ event, handler }) => {
      this.connectionManager.off(event, handler);
    });
    this.connectionListeners = [];
    // 씬 종료와 함께 파괴되는 GameObject 참조는 create()에서 null로 리셋된다.
  }
}